import { useMemo } from "react";

import { CartShippingAddress } from "common/types/carts";

import ConfirmationDialog from "client/common/components/content/ConfirmationDialog";
import { useCurrentDialog } from "client/common/hooks/stackables";
import { useConfirmAddressMutation } from "client/accounts/hooks";
import { useInvalidateAllCartQueries } from "client/carts/hooks/invalidate";
import MapBox from "client/common/components/content/MapBox";
import { Clinic } from "client/clinics/types";

import styles from "../../styles/ExistingAddressConfirmationDialog.module.less";

export type ExistingAddressConfirmationDialogProps = {
  address: CartShippingAddress;
  onConfirm: () => void;
  onDeny: () => void;
  clinic: Clinic;
};

const ExistingAddressConfirmationDialog = ({
  address,
  onConfirm,
  onDeny,
  clinic,
}: ExistingAddressConfirmationDialogProps) => {
  const { close } = useCurrentDialog();
  const invalidateCarts = useInvalidateAllCartQueries();
  const markers = useMemo(() => {
    if (!address.coordinates) {
      return [];
    }
    return [address.coordinates];
  }, [address.coordinates]);

  const handleDeny = () => {
    close();
    onDeny();
  };

  const {
    mutate: confirmAddress,
    isPending: isConfirmLoading,
    isSuccess: isConfirmSuccess,
    error: confirmError,
  } = useConfirmAddressMutation(address.id, {
    onSuccess: () => {
      invalidateCarts();
      onConfirm();
      close();
    },
  });

  return (
    <ConfirmationDialog
      title="Confirm address"
      description={`This address was automatically imported by ${clinic.display_name}. Please confirm it is accurate before proceeding.`}
      confirmText="Looks good"
      denyText="Edit address"
      onDeny={handleDeny}
      onConfirm={confirmAddress}
      isLoading={isConfirmLoading || isConfirmSuccess}
      error={confirmError}
    >
      <MapBox address={address} markers={markers} markerStyle="paw" className={styles.mapbox} />
    </ConfirmationDialog>
  );
};

export default ExistingAddressConfirmationDialog;
